import { saveBlob } from '@/lib/download';

export interface AttendancePdfRecord {
  date: string;
  day: string;
  status: string;
  remarks?: string;
}

export interface AttendancePdfOptions {
  fileName: string;
  instituteName?: string;
  studentName?: string;
  className?: string;
  rollNumber?: string | null;
  academicYear?: string;
  month: string;
  generatedAt: string;
  overallAttendance: string;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  leaveDays: number;
  records: AttendancePdfRecord[];
}

/**
 * Escapes characters for PDF literal strings in standard WinAnsi / ASCII encoding.
 */
function sanitizePdfText(text: string | number | null | undefined): string {
  return String(text ?? '')
    .replace(/—/g, '-')
    .replace(/–/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/•/g, '*')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/[^\x20-\x7E]/g, ' ');
}

type RGB = [number, number, number];

interface PageContext {
  id: number;
  contentsId: number;
  drawRect: (x: number, y: number, w: number, h: number, fillRgb?: RGB | null, strokeRgb?: RGB | null, lineWidth?: number) => void;
  drawLine: (x1: number, y1: number, x2: number, y2: number, strokeRgb?: RGB, lineWidth?: number) => void;
  drawText: (text: string | number | null | undefined, x: number, y: number, options?: { font?: 'regular' | 'bold'; size?: number; color?: RGB }) => void;
  finish: () => void;
}

/**
 * Generates and downloads a formatted student attendance PDF report directly
 * from client-side state without external third-party dependencies or server calls.
 */
export function downloadAttendancePdf(options: AttendancePdfOptions): void {
  const encoder = new TextEncoder();
  const objects: (string | null)[] = [];
  const pages: number[] = [];

  function addObject(content: string | null): number {
    objects.push(content);
    return objects.length;
  }

  const catalogId = addObject(null);
  const pagesId = addObject(null);

  const fontRegularId = addObject(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>'
  );
  const fontBoldId = addObject(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>'
  );

  const pageWidth = 595.28; // A4 standard width in points
  const pageHeight = 841.89; // A4 standard height in points
  const marginX = 40;
  const contentWidth = pageWidth - marginX * 2; // 515.28

  function createPageContext(): PageContext {
    const pageIndex = objects.length + 1;
    const contentsIndex = pageIndex + 1;
    let stream = '';

    const ctx: PageContext = {
      id: pageIndex,
      contentsId: contentsIndex,
      drawRect(x, y, w, h, fillRgb = null, strokeRgb = null, lineWidth = 1) {
        let cmd = '';
        if (lineWidth !== 1) cmd += `${lineWidth.toFixed(2)} w\n`;
        if (fillRgb) cmd += `${fillRgb[0].toFixed(3)} ${fillRgb[1].toFixed(3)} ${fillRgb[2].toFixed(3)} rg\n`;
        if (strokeRgb) cmd += `${strokeRgb[0].toFixed(3)} ${strokeRgb[1].toFixed(3)} ${strokeRgb[2].toFixed(3)} RG\n`;
        cmd += `${x.toFixed(2)} ${y.toFixed(2)} ${w.toFixed(2)} ${h.toFixed(2)} re\n`;
        if (fillRgb && strokeRgb) cmd += 'B\n';
        else if (fillRgb) cmd += 'f\n';
        else if (strokeRgb) cmd += 'S\n';
        stream += cmd;
      },
      drawLine(x1, y1, x2, y2, strokeRgb = [0, 0, 0], lineWidth = 1) {
        stream += `${lineWidth.toFixed(2)} w\n`;
        stream += `${strokeRgb[0].toFixed(3)} ${strokeRgb[1].toFixed(3)} ${strokeRgb[2].toFixed(3)} RG\n`;
        stream += `${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y2.toFixed(2)} l S\n`;
      },
      drawText(text, x, y, opts = {}) {
        const { font = 'regular', size = 10, color = [0, 0, 0] } = opts;
        const fontRef = font === 'bold' ? '/F2' : '/F1';
        const sanitized = sanitizePdfText(text);

        stream += `BT\n${fontRef} ${size} Tf\n`;
        stream += `${color[0].toFixed(3)} ${color[1].toFixed(3)} ${color[2].toFixed(3)} rg\n`;
        stream += `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm\n`;
        stream += `(${sanitized}) Tj\nET\n`;
      },
      finish() {
        const streamBytes = encoder.encode(stream);
        objects[pageIndex - 1] = `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentsIndex} 0 R /Resources << /Font << /F1 ${fontRegularId} 0 R /F2 ${fontBoldId} 0 R >> >> >>`;
        objects[contentsIndex - 1] = `<< /Length ${streamBytes.length} >>\nstream\n${stream}\nendstream`;
        pages.push(pageIndex);
      },
    };

    objects.push(null);
    objects.push(null);
    return ctx;
  }

  // Pre-calculate page counts so footers display accurate "Page X of Y"
  const records = options.records || [];
  const maxRowsPage1 = 26;
  const maxRowsLater = 37;
  const totalPages =
    records.length <= maxRowsPage1
      ? 1
      : 1 + Math.ceil((records.length - maxRowsPage1) / maxRowsLater);

  function drawTableHeader(p: PageContext, y: number): void {
    p.drawRect(marginX, y, contentWidth, 20, [0.94, 0.95, 0.97], [0.82, 0.85, 0.90], 1);
    p.drawText('DATE', marginX + 12, y + 6, { font: 'bold', size: 8, color: [0.25, 0.30, 0.40] });
    p.drawText('DAY', marginX + 120, y + 6, { font: 'bold', size: 8, color: [0.25, 0.30, 0.40] });
    p.drawText('STATUS', marginX + 235, y + 6, { font: 'bold', size: 8, color: [0.25, 0.30, 0.40] });
    p.drawText('REMARKS / DETAILS', marginX + 340, y + 6, { font: 'bold', size: 8, color: [0.25, 0.30, 0.40] });
  }

  function drawFooter(p: PageContext, pageNum: number): void {
    p.drawLine(marginX, 36, marginX + contentWidth, 36, [0.85, 0.88, 0.92], 1);
    p.drawText(
      `This is a computer-generated report from ${options.instituteName || 'Smart School Management System'}.`,
      marginX,
      24,
      { font: 'regular', size: 7.5, color: [0.55, 0.60, 0.68] }
    );
    p.drawText(
      `Page ${pageNum} of ${totalPages}`,
      marginX + contentWidth - 55,
      24,
      { font: 'regular', size: 7.5, color: [0.55, 0.60, 0.68] }
    );
  }

  // Draw Page 1
  let page = createPageContext();
  let currentPage = 1;

  // Top Indigo Decorative Strip
  page.drawRect(0, pageHeight - 5, pageWidth, 5, [0.31, 0.27, 0.90]);

  // School Name & Report Header
  page.drawText(options.instituteName || 'Smart School Management System', marginX, 795, {
    font: 'bold',
    size: 15,
    color: [0.06, 0.09, 0.16],
  });
  page.drawText('STUDENT ATTENDANCE REPORT', marginX, 778, {
    font: 'bold',
    size: 11,
    color: [0.31, 0.27, 0.90],
  });
  page.drawText(
    `Monthly attendance details and summary for ${options.month}`,
    marginX,
    765,
    { font: 'regular', size: 8.5, color: [0.45, 0.50, 0.58] }
  );
  page.drawLine(marginX, 755, marginX + contentWidth, 755, [0.85, 0.88, 0.92], 1);

  // Student Details Box (Y: 665 to 740, H: 75)
  const boxY = 665;
  const boxH = 75;
  page.drawRect(marginX, boxY, contentWidth, boxH, [0.97, 0.98, 0.99], [0.85, 0.88, 0.92], 1);

  // Column 1
  page.drawText('Student Name:', marginX + 12, boxY + 52, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.studentName || 'Student', marginX + 85, boxY + 52, { font: 'bold', size: 9.5, color: [0.06, 0.09, 0.16] });

  page.drawText('Class & Section:', marginX + 12, boxY + 32, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.className || '-', marginX + 85, boxY + 32, { font: 'regular', size: 9.5, color: [0.06, 0.09, 0.16] });

  page.drawText('Roll Number:', marginX + 12, boxY + 12, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.rollNumber ? `#${options.rollNumber}` : '-', marginX + 85, boxY + 12, { font: 'regular', size: 9.5, color: [0.06, 0.09, 0.16] });

  // Column 2
  const col2X = marginX + 270;
  page.drawText('Academic Year:', col2X, boxY + 52, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.academicYear || '-', col2X + 85, boxY + 52, { font: 'regular', size: 9.5, color: [0.06, 0.09, 0.16] });

  page.drawText('Report Month:', col2X, boxY + 32, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.month || '-', col2X + 85, boxY + 32, { font: 'bold', size: 9.5, color: [0.06, 0.09, 0.16] });

  page.drawText('Generated Date:', col2X, boxY + 12, { font: 'bold', size: 8.5, color: [0.45, 0.50, 0.58] });
  page.drawText(options.generatedAt || '-', col2X + 85, boxY + 12, { font: 'regular', size: 8.5, color: [0.45, 0.50, 0.58] });

  // 5 KPI Metric Cards (Y: 600 to 650, H: 50)
  const kpiY = 600;
  const kpiH = 50;
  const kpiGap = 8;
  const kpiW = (contentWidth - kpiGap * 4) / 5;

  const cards: { label: string; val: string; bg: RGB; border: RGB; text: RGB }[] = [
    { label: 'Overall Rate', val: options.overallAttendance ?? '-', bg: [0.93, 0.98, 0.95], border: [0.65, 0.90, 0.75], text: [0.02, 0.48, 0.30] },
    { label: 'Present Days', val: `${options.presentDays ?? 0} Days`, bg: [0.94, 0.97, 1.00], border: [0.73, 0.85, 1.00], text: [0.01, 0.45, 0.72] },
    { label: 'Absent Days', val: `${options.absentDays ?? 0} Days`, bg: [1.00, 0.94, 0.95], border: [1.00, 0.75, 0.79], text: [0.88, 0.11, 0.28] },
    { label: 'Late Days', val: `${options.lateDays ?? 0} Days`, bg: [1.00, 0.98, 0.92], border: [0.99, 0.88, 0.65], text: [0.80, 0.45, 0.03] },
    { label: 'Leave Days', val: `${options.leaveDays ?? 0} Days`, bg: [0.96, 0.94, 1.00], border: [0.87, 0.80, 1.00], text: [0.49, 0.23, 0.93] },
  ];

  cards.forEach((c, idx) => {
    const cX = marginX + idx * (kpiW + kpiGap);
    page.drawRect(cX, kpiY, kpiW, kpiH, c.bg, c.border, 1);
    page.drawText(c.label, cX + 8, kpiY + 33, { font: 'bold', size: 7.5, color: c.text });
    page.drawText(c.val, cX + 8, kpiY + 13, { font: 'bold', size: 10.5, color: c.text });
  });

  // Daily Log Heading (baseline: 572, gap above: 28pt)
  const headingY = 572;
  page.drawText('DAILY ATTENDANCE LOG', marginX, headingY, { font: 'bold', size: 9.5, color: [0.15, 0.20, 0.28] });

  // Table Header (Y: 542 to 562, H: 20, gap from heading baseline: 10pt)
  const headerY = 542;
  drawTableHeader(page, headerY);

  if (records.length === 0) {
    // Empty Box (Y: 482 to 542, H: 60)
    const emptyBoxH = 60;
    const emptyBoxY = headerY - emptyBoxH;
    page.drawRect(marginX, emptyBoxY, contentWidth, emptyBoxH, [0.98, 0.99, 1.0], [0.88, 0.90, 0.94], 1);

    page.drawRect(marginX + 20, emptyBoxY + 34, 6, 6, [0.31, 0.27, 0.90], null);
    page.drawText(
      `No attendance records entered for ${options.month} yet.`,
      marginX + 32,
      emptyBoxY + 34,
      { font: 'bold', size: 9, color: [0.20, 0.25, 0.35] }
    );
    page.drawText(
      'The class register has not been marked by the school for this period. Daily entries will appear here once attendance is recorded.',
      marginX + 32,
      emptyBoxY + 18,
      { font: 'regular', size: 8, color: [0.45, 0.50, 0.60] }
    );
  } else {
    let curY = headerY - 18;
    records.forEach((row, i) => {
      // If running out of page height, break to next page
      if (curY < 50) {
        drawFooter(page, currentPage);
        page.finish();

        page = createPageContext();
        currentPage++;

        page.drawRect(0, pageHeight - 5, pageWidth, 5, [0.31, 0.27, 0.90]);
        page.drawText(options.instituteName || 'Smart School Management System', marginX, 805, {
          font: 'bold',
          size: 12,
          color: [0.06, 0.09, 0.16],
        });
        page.drawText(
          `Attendance Log for ${options.studentName || 'Student'} (${options.month}) - Continued`,
          marginX,
          790,
          { font: 'regular', size: 8.5, color: [0.45, 0.50, 0.58] }
        );
        page.drawLine(marginX, 782, marginX + contentWidth, 782, [0.85, 0.88, 0.92], 1);

        const page2HeaderY = 750;
        drawTableHeader(page, page2HeaderY);
        curY = page2HeaderY - 18;
      }

      const rowBg: RGB = i % 2 === 0 ? [1, 1, 1] : [0.98, 0.99, 1.0];
      page.drawRect(marginX, curY, contentWidth, 18, rowBg, [0.90, 0.92, 0.95], 0.5);

      page.drawText(row.date, marginX + 12, curY + 5, { font: 'regular', size: 8, color: [0.15, 0.20, 0.28] });
      page.drawText(row.day, marginX + 120, curY + 5, { font: 'regular', size: 8, color: [0.38, 0.44, 0.52] });

      let statusColor: RGB = [0.02, 0.48, 0.30]; // Present
      let statusBg: RGB = [0.93, 0.98, 0.95];
      let statusBorder: RGB = [0.65, 0.90, 0.75];
      const st = row.status.toLowerCase();
      if (st.includes('absent')) {
        statusColor = [0.88, 0.11, 0.28];
        statusBg = [1.00, 0.94, 0.95];
        statusBorder = [1.00, 0.75, 0.79];
      } else if (st.includes('late')) {
        statusColor = [0.80, 0.45, 0.03];
        statusBg = [1.00, 0.98, 0.92];
        statusBorder = [0.99, 0.88, 0.65];
      } else if (st.includes('leave')) {
        statusColor = [0.49, 0.23, 0.93];
        statusBg = [0.96, 0.94, 1.00];
        statusBorder = [0.87, 0.80, 1.00];
      } else if (st.includes('half')) {
        statusColor = [0.26, 0.38, 0.94];
        statusBg = [0.93, 0.95, 1.00];
        statusBorder = [0.75, 0.82, 0.98];
      } else if (st.includes('weekend') || st.includes('holiday')) {
        statusColor = [0.55, 0.60, 0.68];
        statusBg = [0.95, 0.96, 0.98];
        statusBorder = [0.85, 0.88, 0.92];
      }

      // Styled status badge pill
      const badgeW = 66;
      const badgeH = 13;
      const badgeX = marginX + 235;
      const badgeY = curY + 2.5;
      page.drawRect(badgeX, badgeY, badgeW, badgeH, statusBg, statusBorder, 0.5);
      page.drawText(row.status, badgeX + 6, badgeY + 3.5, { font: 'bold', size: 7.5, color: statusColor });

      const remarkText =
        row.remarks ||
        (st.includes('weekend')
          ? 'Weekend'
          : st.includes('holiday')
            ? 'Holiday'
            : st.includes('leave')
              ? 'Approved Leave'
              : 'Regular Attendance');
      page.drawText(remarkText, marginX + 340, curY + 5, { font: 'regular', size: 7.5, color: [0.45, 0.50, 0.58] });

      curY -= 18;
    });
  }

  // Finish last page
  drawFooter(page, currentPage);
  page.finish();

  // Assemble Complete PDF
  objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pages.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;

  let buffer = '%PDF-1.4\n%\xE2\xE3\xCF\xD3\n';
  const offsets: number[] = [];

  for (let i = 0; i < objects.length; i++) {
    offsets.push(encoder.encode(buffer).length);
    buffer += `${i + 1} 0 obj\n${objects[i]}\nendobj\n`;
  }

  const xrefOffset = encoder.encode(buffer).length;
  buffer += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;

  for (const offset of offsets) {
    buffer += `${String(offset).padStart(10, '0')} 00000 n \n`;
  }

  buffer += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  const uint8 = encoder.encode(buffer);
  const blob = new Blob([uint8], { type: 'application/pdf' });
  saveBlob(blob, options.fileName);
}
